import { datadogFeatureFlags } from '@/const/flags';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

export const initializeDatadogRUM = () => {
  if (datadogRum.getInternalContext()) return;

  // Initialize Datadog logs
  datadogLogs.init({
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
    site: process.env.NEXT_PUBLIC_DATADOG_SITE,
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME,
    env: process.env.VERCEL_ENV || 'local',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100
  });

  datadogRum.init({
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID || '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
    site: process.env.NEXT_PUBLIC_DATADOG_SITE,
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME,
    env: process.env.VERCEL_ENV || 'local',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    enableExperimentalFeatures: ['feature_flags']
  });

  Object.entries(datadogFeatureFlags).forEach(([key, value]) => {
    datadogRum.addFeatureFlagEvaluation(key, value);
  });
};

// Helper function to log Ella errors
export const logEllaError = (
  error: Error | string,
  context: Record<string, any> = {}
) => {
  // `datadogLogs` is a browser SDK: its logger reads `document`, so calling it
  // on the server throws a ReferenceError. Most callers sit in catch blocks
  // (ella_server.ts), where that turned a handled failure into a crashed
  // render. Emit a JSON line instead (Vercel logs -> Datadog, as in
  // `@/util/logger`), and never let logging itself throw.
  if (typeof window === 'undefined') {
    try {
      console.error(
        JSON.stringify({
          level: 'error',
          message: typeof error === 'string' ? error : error.message,
          stack: typeof error === 'string' ? undefined : error.stack,
          ...context,
          category: 'ella_error',
          timestamp: new Date().toISOString()
        })
      );
    } catch {
      console.error('logEllaError failed to serialize', String(error));
    }
    return;
  }

  datadogLogs.logger.error(typeof error === 'string' ? error : error.message, {
    error: typeof error === 'string' ? new Error(error) : error,
    ...context,
    category: 'ella_error'
  });
};

/**
 * Cancellation-flow warnings (VIVE-3443). `forwardErrorsToLogs` only forwards
 * console.error, so these member-impacting-but-non-error signals (e.g. "the
 * member finished the flow but the webhook's effect never became visible")
 * would otherwise die in the browser console. Tagged for Datadog queries:
 * @category:cancellation_flow status:warn
 */
export const logCancellationFlowWarning = (
  message: string,
  context: Record<string, any> = {}
) => {
  datadogLogs.logger.warn(message, {
    ...context,
    category: 'cancellation_flow'
  });
};

export const closeDatadogRUM = () => {
  datadogRum.stopSession();
  datadogRum.clearUser();
};
